import { useCallback, useState } from 'react'
import { trackEvent } from '../../utils/productAnalytics'
import { trackLandingCta } from './landingUtils'
import {
  type BlogShareChannel,
  canUseNativeShare,
  copyBlogShareLink,
  shareByEmail,
  shareNative,
  shareToTelegram,
  shareToVk,
  buildBlogShareUrl,
  buildBlogShareMessage,
} from '../../utils/blogShare'

type BlogShareBarProps = {
  title: string
  excerpt?: string | null
  slug: string
  coverImageUrl?: string | null
  /** Visual placement for analytics */
  placement?: 'header' | 'footer'
}

type ShareAction = {
  id: BlogShareChannel
  label: string
  icon: string
}

const SHARE_ACTIONS: ShareAction[] = [
  { id: 'copy', label: 'Копировать ссылку', icon: 'link' },
  { id: 'telegram', label: 'Telegram', icon: 'send' },
  { id: 'vk', label: 'ВКонтакте', icon: 'groups' },
  { id: 'email', label: 'Почта', icon: 'mail' },
]

function trackBlogShare(channel: BlogShareChannel, slug: string, placement: string) {
  trackLandingCta('blog', `share_${channel}_${placement}`)
  trackEvent(
    'blog_share',
    { channel, slug, placement },
    { surface: 'blog', entity_type: 'blog_post', entity_id: slug },
  )
}

export default function BlogShareBar({
  title,
  excerpt,
  slug,
  coverImageUrl,
  placement = 'footer',
}: BlogShareBarProps) {
  const [copied, setCopied] = useState(false)
  const showNative = canUseNativeShare()

  const handleCopy = useCallback(async () => {
    const url = buildBlogShareUrl(slug, 'copy')
    const text = buildBlogShareMessage(title, excerpt, url)
    const ok = await copyBlogShareLink(text)
    if (ok) {
      setCopied(true)
      trackBlogShare('copy', slug, placement)
      window.setTimeout(() => setCopied(false), 2000)
    }
  }, [slug, placement])

  const handleAction = useCallback(async (channel: BlogShareChannel) => {
    switch (channel) {
      case 'copy':
        await handleCopy()
        return
      case 'telegram':
        shareToTelegram(title, excerpt, slug)
        break
      case 'vk':
        shareToVk(title, excerpt, slug, coverImageUrl)
        break
      case 'email':
        shareByEmail(title, excerpt, slug)
        break
      case 'native':
        await shareNative(title, excerpt, slug)
        break
      default:
        return
    }
    trackBlogShare(channel, slug, placement)
  }, [title, excerpt, slug, coverImageUrl, placement, handleCopy])

  const handleNativeShare = useCallback(async () => {
    const ok = await shareNative(title, excerpt, slug)
    if (ok) trackBlogShare('native', slug, placement)
  }, [title, excerpt, slug, placement])

  return (
    <aside className={`blog-share ${placement === 'header' ? 'blog-share--header' : 'blog-share--footer'}`} aria-label="Поделиться статьёй">
      <span className="blog-share-label">Поделиться</span>

      <div className="blog-share-actions">
        {showNative ? (
          <button
            type="button"
            className="blog-share-btn blog-share-btn--native"
            onClick={handleNativeShare}
            aria-label="Поделиться через приложение"
          >
            <span className="material-symbols-outlined" aria-hidden="true">ios_share</span>
            <span>Поделиться</span>
          </button>
        ) : null}

        {SHARE_ACTIONS.map((action) => (
          <button
            key={action.id}
            type="button"
            className={`blog-share-btn${action.id === 'copy' && copied ? ' blog-share-btn--copied' : ''}`}
            onClick={() => handleAction(action.id)}
            aria-label={action.id === 'copy' && copied ? 'Ссылка скопирована' : action.label}
            title={action.id === 'copy' && copied ? 'Ссылка скопирована' : action.label}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              {action.id === 'copy' && copied ? 'check' : action.icon}
            </span>
            <span className="blog-share-btn-text">
              {action.id === 'copy' && copied ? 'Скопировано' : action.label}
            </span>
          </button>
        ))}
      </div>

      {copied ? (
        <p className="blog-share-toast" role="status">Текст скопирован в буфер обмена</p>
      ) : null}
    </aside>
  )
}
